// @ts-strict-ignore
import isEqual from 'lodash/isEqual';
import snakeCase from 'lodash/snakeCase';
import merge from 'lodash/merge';
import capitalize from 'lodash/capitalize';

export function parseBooleanString(s: 'true' | 'false' | string) {
  return s === 'true';
}

export function isObjectEmpty(o: Object): boolean {
  return typeof o === 'object' && Object.keys(o).length === 0;
}

export function areEqual(prev: any, next: any) {
  return isEqual(prev, next);
}

export function areDifferent(prev: any, next: any) {
  return !isEqual(prev, next);
}

/**
 * removes the **top-level** properties of an object.
 * @returns a new object without the specified properties
 */
export function omitProperties<T, PS extends keyof T>(obj: T, properties: PS[]): Omit<T, PS> {
  if (!properties || properties.length === 0) {
    return structuredClone(obj);
  }

  const filteredEntries = Object.entries(obj).filter(([key]) => !properties.includes(key as PS));
  return Object.fromEntries(filteredEntries) as Omit<T, PS>;
}

/**
 * deeply removes properties on an object based on the result of the filter function
 * note: it does not handle class-based objects
 */
export function pruneObjectDeep(
  obj: unknown,
  filterFn: (key: string, value: unknown) => boolean,
): unknown {
  if (Array.isArray(obj)) {
    return obj.map(item => pruneObjectDeep(item, filterFn));
  }

  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  const result = {} as unknown;
  Object.entries(obj).forEach(([key, value]) => {
    if (typeof value === 'object' && value !== null) {
      if (!filterFn(key, value)) {
        result[key] = pruneObjectDeep(value, filterFn);
      }
      // filter is true for properties to exclude
    } else if (!filterFn(key, value)) {
      result[key] = value;
    }
  });
  return result;
}


/**
 * Grab all nested values in an object at a specified key
*
* @param source source object
* @param desiredKey key to look by
* @returns array of strings with values found at the specified key
*/
export function extractValuesByKey(source: any, desiredKey: string) {
  const result: string[] = [];

  function recurse(_source: any) {
    Object.entries(_source).forEach(([key, value]) => {
      if (key === desiredKey) {
        result.push(String(value));
      }
      if (value !== null && typeof value === 'object') {
        recurse(value);
      }
    });
  }

  recurse(source);
  return result;
}


export function getBrowserOS() {
  const { userAgent } = navigator;
  // test against "macintosh" or "windows", case insensitive
  const isMacOS = /macintosh/i.test(userAgent);
  const isWindows = /windows/i.test(userAgent);

  if (isMacOS) {
    return 'mac';
  }

  if (isWindows) {
    return 'windows';
  }

  return null;
}

/**
 * Returns a new string where values in message are replaced with a matching token's value.
 * Uses token key as the item to match.
*/
export function parseMessage(message: string, tokens: Record<string, string>) {
  let copiedMessage = message;
  Object.entries(tokens).forEach(entry => {
    copiedMessage = copiedMessage.replaceAll(entry[0], entry[1]);
  });
  return copiedMessage;
}

// transforms top-level keys of an object (does not recurse)
export function toSnakeCase(obj: object) {
  const converted = {};
  for (const key of Object.keys(obj)) {
    converted[snakeCase(key)] = obj[key];
  }
  return converted;
}


export function mergeDeep<L, R>(target: L, source: R): L & R {
  return merge({}, target, source);
}

/**
 * Convert snake_case string to Capitalized Words
 * e.g., "journey_router" -> "Journey Router"
 */
export const formatSnakeCaseToTitle = (value: string): string => (
  value.split('_').map(capitalize).join(' ')
);
